import type { FrameCore, InputSnapshot, CoreAvSpec } from './types';
import type { Renderer } from './video/Renderer';
import { AudioOutput } from './audio/AudioOutput';
import { RewindBuffer } from './RewindBuffer';

export interface FrameDriverOptions {
  core: FrameCore;
  renderer: Renderer;
  audio: AudioOutput;
  pollInput: () => InputSnapshot;
  onFps?: (fps: number) => void;
  onError?: (e: Error) => void;
  /** 巻き戻しが無効化されたときの通知（ステート非対応・サイズ超過） */
  onRewindUnavailable?: (reason: string) => void;
}

/** 巻き戻し中に何フレームおきに 1 つ遡るか。1 なら記録間隔と同じ速度で戻る。 */
const REWIND_STEP_FRAMES = 1;

/** 遅延が大きすぎるとき一度に取り戻すフレーム数の上限 */
const MAX_CATCHUP_FRAMES = 4;
/** 目標オーディオバッファ長 (秒)。これを基準に速度を微調整する。 */
const TARGET_AUDIO_LATENCY = 0.08;

/**
 * requestAnimationFrame を心拍としつつ、オーディオバッファの残量を見て
 * フレーム発行数を微調整する駆動ループ。
 *
 * rAF だけだと 60Hz 以外のディスプレイ（iPhone の 120Hz ProMotion や
 * 外部 4K/30Hz）で速度が狂うため、経過時間ベースのアキュムレータを使う。
 * さらにオーディオ残量でフィードバックをかけることで、長時間走らせても
 * 映像と音がずれていかない。
 */
export class FrameDriver {
  private opts: FrameDriverOptions;
  private spec: CoreAvSpec | null = null;

  private rafId = 0;
  private lastTime = 0;
  private accumulator = 0;
  private _running = false;
  private speed = 1;

  private fpsFrames = 0;
  private fpsLast = 0;

  private rewind: RewindBuffer | null = null;
  private rewinding = false;
  private rewindStepCounter = 0;
  private rewindNotified = false;

  constructor(opts: FrameDriverOptions) {
    this.opts = opts;
  }

  /** 巻き戻しを有効にする。null を渡すと無効化。 */
  setRewindBuffer(buffer: RewindBuffer | null): void {
    this.rewind = buffer;
    this.rewinding = false;
    this.rewindNotified = false;
  }

  get rewindBuffer(): RewindBuffer | null {
    return this.rewind;
  }

  /**
   * 巻き戻しの開始／終了。
   * 巻き戻し中は音を止める（逆再生した音は聞けたものではないため）。
   */
  setRewinding(on: boolean): void {
    if (this.rewinding === on) return;
    this.rewinding = on && Boolean(this.rewind?.enabled);
    this.rewindStepCounter = 0;
    this.opts.audio.flush();
    this.opts.audio.setMuted(this.rewinding);
  }

  get isRewinding(): boolean {
    return this.rewinding;
  }

  /** ステート読み込みなどで時系列が飛んだときに呼ぶ */
  resetRewind(): void {
    this.rewind?.reset();
  }

  setSpec(spec: CoreAvSpec): void {
    this.spec = spec;
  }

  get running(): boolean {
    return this._running;
  }

  setSpeed(mult: number): void {
    const next = Math.max(0.1, Math.min(8, mult));
    if (next === this.speed) return;
    this.speed = next;
    // 早送り中に溜まった PCM を残すと、通常速度に戻しても割れた音が流れ続ける。
    this.opts.audio.flush();
    this.accumulator = 0;
    this.lastTime = performance.now();
  }

  start(): void {
    if (this._running) return;
    this._running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.fpsLast = this.lastTime;
    this.fpsFrames = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    if (!this._running) return;
    this._running = false;
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.opts.audio.flush();
  }

  /** 1 フレームだけ進める（ポーズ中のコマ送り用） */
  step(): void {
    if (this._running) return;
    this.runOneFrame();
  }

  private tick = (now: number): void => {
    if (!this._running) return;
    this.rafId = requestAnimationFrame(this.tick);

    const spec = this.spec;
    if (!spec) return;

    let delta = (now - this.lastTime) / 1000;
    this.lastTime = now;

    // タブ復帰直後などの巨大な delta で暴走させない
    if (delta > 0.25) delta = 0.25;

    const frameTime = 1 / (spec.fps * this.speed);
    this.accumulator += delta;

    // --- オーディオ残量によるフィードバック ---------------------------------
    // バッファが薄い → 少し多めに回す / 厚い → 少し休む。
    // ±2% 程度の微調整に留めることでピッチの揺れを聴感上ゼロにする。
    const stats = this.opts.audio.getStats();
    if (this.opts.audio.isReady && stats.capacity > 1) {
      const drift = stats.latency - TARGET_AUDIO_LATENCY;
      if (drift > 0.05) this.accumulator -= frameTime * 0.02;
      else if (drift < -0.02) this.accumulator += frameTime * 0.02;
    }

    let frames = 0;
    while (this.accumulator >= frameTime && frames < MAX_CATCHUP_FRAMES) {
      this.accumulator -= frameTime;
      frames++;
      this.runOneFrame(frames === 1);
    }

    // 積み残しが多すぎるなら諦めて捨てる（スローモーション化を防ぐ）
    if (this.accumulator > frameTime * MAX_CATCHUP_FRAMES) {
      this.accumulator = 0;
    }

    this.fpsFrames += frames;
    if (now - this.fpsLast >= 1000) {
      this.opts.onFps?.((this.fpsFrames * 1000) / (now - this.fpsLast));
      this.fpsFrames = 0;
      this.fpsLast = now;
    }
  };

  /**
   * @param render 追いつき処理中の中間フレームは描画をスキップできる。
   *               音は必ず全フレーム分取り出す（欠けると途切れるため）。
   */
  private runOneFrame(render = true): void {
    const { core, renderer, audio, pollInput, onError } = this.opts;
    try {
      if (this.rewinding) {
        this.rewindOneStep(render);
        return;
      }

      const input = pollInput();
      const frame = core.runFrame(input);
      if (render && frame.width > 0 && frame.height > 0) {
        renderer.draw(frame);
      }
      const chunk = core.drainAudio();
      if (chunk && chunk.samples.length > 0) {
        audio.push(chunk);
      }

      this.captureRewindPoint();
    } catch (e) {
      this.stop();
      onError?.(e instanceof Error ? e : new Error(String(e)));
    }
  }

  private captureRewindPoint(): void {
    const buffer = this.rewind;
    if (!buffer) return;

    buffer.tick(() => this.opts.core.saveState());

    // 巻き戻しが使えないと分かった時点で 1 度だけ通知する
    if (!buffer.enabled && !this.rewindNotified) {
      this.rewindNotified = true;
      this.rewinding = false;
      this.opts.audio.setMuted(false);
      this.opts.onRewindUnavailable?.(buffer.disabled ?? '巻き戻しを利用できません');
    }
  }

  /**
   * 1 つ前の記録地点へ戻る。
   *
   * 戻したあと 1 フレームだけ空入力で進めるのは、その時点の映像を得るため。
   * コアによっては loadState 直後にフレームバッファが更新されず、
   * 走らせないと絵が出ない。
   */
  private rewindOneStep(render: boolean): void {
    const buffer = this.rewind;
    if (!buffer) return;

    if (++this.rewindStepCounter < REWIND_STEP_FRAMES) return;
    this.rewindStepCounter = 0;

    const state = buffer.pop();
    if (!state) {
      // これ以上戻れない。巻き戻しを解除して通常再生へ戻す。
      this.setRewinding(false);
      return;
    }

    const { core, renderer } = this.opts;
    core.loadState(state);

    const frame = core.runFrame(NEUTRAL_INPUT);
    if (render && frame.width > 0 && frame.height > 0) {
      renderer.draw(frame);
    }
    // 巻き戻し中の音は捨てる
    core.drainAudio();
  }
}

/** 巻き戻し中に流し込む空入力。毎回作らないよう使い回す。 */
const NEUTRAL_INPUT: InputSnapshot = {
  buttons: new Uint16Array(4),
  analog: { leftX: 0, leftY: 0, rightX: 0, rightY: 0 },
};
